/**
 * useAlgoSupply — V2.K.5
 *
 * Fetches ALGO supply data from CoinGecko :
 *  - circulating_supply : tokens currently distributed
 *  - total_supply       : equals max_supply for Algorand — the entire
 *                         10 B was minted at genesis (2019)
 *  - max_supply         : 10 000 000 000 ALGO (hard cap, immutable)
 *
 * Algorand is structurally simple :
 *   - All 10 B ALGO were minted at genesis (2019).
 *   - There is NO inflation, NO mint, NO burn as monetary policy.
 *   - The remaining ~1 B ALGO held by the Foundation enters
 *     circulation progressively through distribution programs
 *     (Staking Rewards since Algorand 4.0 / Jan 2025; previously the
 *     Governance Rewards program, which ended at GP14 in Q1 2025
 *     after distributing 687.5 M ALGO).
 *   - Foundation expects >99 % of the supply in circulation by 2030.
 *
 * Refresh : every 5 minutes.
 */

import { useQuery } from '@tanstack/react-query';

export interface AlgoSupplyData {
  circulating: number;
  total: number;
  max: number;
  /** % of the 10 B cap already in circulation. */
  distributedPct: number;
  /** ALGO still held by the Foundation / pre-distribution. */
  remainingToDistribute: number;
  /** Published reference value of ALGO actively online-staked
   *  (per algorand.co 2025 roadmap blog — ~2 B early 2026). */
  onlineStakeReference: number;
  /** % of circulating supply currently online-staked. */
  onlineStakePctOfCirculating: number;
  lastUpdated: string | null;
}

// 10 B hard cap — minted in full at genesis (Jun 2019). Immutable.
const ALGO_MAX_SUPPLY = 10_000_000_000;
// Reference values from algorand.co/blog/2025-on-algorand-roadmap-progress
// (online stake doubled from ~1 B to ~2 B ALGO in just over a year).
const ALGO_ONLINE_STAKE_REFERENCE = 2_000_000_000;

async function fetchAlgoSupply(): Promise<AlgoSupplyData> {
  const response = await fetch(
    'https://api.coingecko.com/api/v3/coins/algorand?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false',
  );
  if (!response.ok) {
    throw new Error(`CoinGecko returned ${response.status}`);
  }
  const json = await response.json();
  const md = json.market_data || {};
  const circulating = Number(md.circulating_supply) || 0;
  const total = Number(md.total_supply) || ALGO_MAX_SUPPLY;
  const max = Number(md.max_supply) || ALGO_MAX_SUPPLY;

  return {
    circulating,
    total,
    max,
    distributedPct: max > 0 ? (circulating / max) * 100 : 0,
    remainingToDistribute: Math.max(0, max - circulating),
    onlineStakeReference: ALGO_ONLINE_STAKE_REFERENCE,
    onlineStakePctOfCirculating: circulating > 0
      ? (ALGO_ONLINE_STAKE_REFERENCE / circulating) * 100
      : 0,
    lastUpdated: json.last_updated || null,
  };
}

export function useAlgoSupply() {
  return useQuery({
    queryKey: ['algo', 'supply'],
    queryFn: fetchAlgoSupply,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
  });
}
